import ISceneObject from "../model/sceneObject/ISceneObject";
import IContextDocument from "../model/document/IContextDocument";
import Document from "../model/document/Document";
import ProjectDocument from "../model/document/ProjectDocument";
import DocumentEventType from "../enums/DocumentEventType";
import { useSceneObjectScopeStore } from "../store/sceneObjectScopeStore";

export default class SceneDocumentManager {

    #sceneDocuments: IContextDocument[] = []; // To store documents that have been added to the scene
    #rotationOffset: number = 0; // Current rotation offset for annotation direction

    constructor() {
        // Subscribed directly to the store (not routed through a React effect) so scope changes are
        // applied the instant they happen, regardless of React's render/commit timing relative to
        // when documents get staged. This is what makes routes like /checklist-item/:id work even
        // though the correct scope (a single scene object uuid) can only be resolved after the
        // document is already staged - the filter-at-staging-time approach can't handle that
        // ordering, but reapplying visibility whenever the scope changes can.
        useSceneObjectScopeStore.subscribe((state) => {
            const scope = state.scopedSceneObjectUuids ?? undefined;
            this.#sceneDocuments.forEach(projectDoc => projectDoc.document.applyScope(scope));
        });
    }

    getContextDocument(doc: Document) : IContextDocument | undefined {
        return this.#sceneDocuments.find(sc => sc.document === doc);
    }

    // Intentionally bypasses the scene object scope: an item just created during the current
    // session (e.g. via AddChecklistItemCmd) must always be shown, even if a scope set elsewhere
    // in the tree would otherwise exclude it - it's the item the user is actively working on, not
    // part of the "other items" clutter the scope is meant to suppress.
    listenerForAdd = (doc: Document, sceneObject?: ISceneObject) => {
        if (sceneObject) {
            sceneObject.addToScene();

            sceneObject.onAnnotationDirectionChange(this.#rotationOffset); // Update the orientation of the annotation text
        }
    }

    listenerForRemove = (doc: Document, sceneObject?: ISceneObject) => {
        if (sceneObject) {
            sceneObject.removeFromScene();
        }
    }

    saveDocument(document: Document) {
        const contextDoc = this.getContextDocument(document);
        if (contextDoc) {
            // Imported lazily to avoid a load-order-dependent circular import with servicesStateStore.ts,
            // which imports SceneDocumentManager to construct it eagerly.
            import("../store/servicesStateStore").then(({ useServicesState }) => {
                const prv = useServicesState.getState().dataProvider;
                if (prv)
                    prv.saveDocument(contextDoc);
                else
                    console.error("Failed to save document, data provider missing");
            });
        }
        else {
            throw "Cannot find project doc to save";
        }
    }

    addToScene(documents: IContextDocument[]) {
        console.info(`SceneDocumentManager: Adding ${documents.length} documents to the scene.`);
        const scope = useSceneObjectScopeStore.getState().scopedSceneObjectUuids ?? undefined;
        documents.forEach(projectDoc => {
            projectDoc.document.addToScene();
            projectDoc.document.applyScope(scope);

            projectDoc.document.addEventListener(DocumentEventType.Add, this.listenerForAdd);
            projectDoc.document.addEventListener(DocumentEventType.Remove, this.listenerForRemove);

            this.#sceneDocuments.push(projectDoc);
        });
    }

    removeFromScene(documents: IContextDocument[]) {
        console.info(`SceneDocumentManager: Removing ${documents.length} documents from the scene.`);
        documents.forEach(projectDoc => {
            // TODO: Remove from scene?

            projectDoc.document.removeEventListener(DocumentEventType.Add, this.listenerForAdd);
            projectDoc.document.removeEventListener(DocumentEventType.Remove, this.listenerForRemove);

            const docIndex = this.#sceneDocuments.indexOf(projectDoc);
            if (docIndex > -1)
                this.#sceneDocuments.splice(docIndex, 1);
        });
    }

    annotationDirectionChanged(rotationOffset: number) {
        this.#rotationOffset = rotationOffset;
        this.#sceneDocuments.forEach(projectDoc => {
            projectDoc.document.onAnnotationDirectionChange(rotationOffset);
        });
    }
};
